import {callAPI} from '@/store/utils'; // eslint-ignore-line
import {resolveCategoryErrorMessage} from '@/errors/categoryErrorMappings.js';

const FYGR_COMPANY = 'FYGR_COMPANY';

const GENERIC_CATEGORY_ERROR = 'Veuillez vérifier votre saisie';

const categoryErrorMessage = (err, name) =>
  resolveCategoryErrorMessage({error: err, name}) ||
  err?.userMessage ||
  GENERIC_CATEGORY_ERROR;

const recursiveEnabledCategories = (list) => {
  const filteredList = list.reduce((acc, el) => {
    if (el.forecast_enabled) {
      return [...acc, el.id];
    }
    if (el.list && el.list.length) {
      const sublist = recursiveEnabledCategories(el.list);
      return [...acc, ...sublist];
    }
    return acc;
  }, []);
  return filteredList.filter((el) => !!el);
};

export default {
  namespaced: true,
  state: {
    loading: false,
    locked: false,
    loadingTitle: '',
    categories: [],
    appLoaded: false,
    sortedCats: false,
    sortedSubcats: false,
    notifications: [],
    scenarios: null,
    colors: [],
    reloading: false,
    tou: null,
    tooManyConnections: false,
    dashboardSpecial: 0,
    selectedCompany: null,
    silence: false,
    forceScenarios: false,
    suppressOnboarding: false,
    globalLoading: false,
    scaleRatio: 1,
    currentBridgeItem: null,
    inAppMessage: '',
  },
  mutations: {
    unauthApp(state) {
      state.loading = false;
      state.loadingTitle = '';
      state.categories = [];
      state.appLoaded = false;
      state.sortedCats = false;
      state.sortedSubcats = false;
      state.notifications = [];
      state.scenarios = null;
      state.colors = [];
      state.reloading = false;
      state.tooManyConnections = false;
      state.dashboardSpecial = 0;
      state.silence = false;
      state.globalLoading = false;
      state.currentBridgeItem = null;
      state.inAppMessage = '';
    },
    updateCategoryForecast(state, categories) {
      state.categories = JSON.parse(JSON.stringify(categories));
    },
    setSelectedCompany(state, selectedCompany) {
      state.selectedCompany = selectedCompany;
    },
    setSuppressOnboarding(state, suppressOnboarding) {
      state.suppressOnboarding = suppressOnboarding;
    },
    setScalingCoeff(state, data) {
      const {zoom, dpr} = data;
      // Values should be the same as in calculator.js . This way we will be aligned with var(--px)
      let result = 1;
      switch (dpr) {
        case 1.5:
          result = 1.5;
          break;
        case 1.6:
          result = 1.6;
          break;
        case 1.79:
          result = 1.79;
          break;
        case 2.25:
          result = 1.1;
          break;
        case 2.5:
          result = 1.25;
          break;
        case 4:
          result = 2;
          break;

        default:
          result = 1;
      }
      state.scaleRatio = result * (100 / zoom);
    },
    setSilence(state) {
      state.silence = !state.silence;
    },
    setColors(state, colors) {
      state.colors = colors;
    },
    setBridgeCurrentItem(state, currentBridgeItem) {
      state.currentBridgeItem = currentBridgeItem;
    },
    updateScenario(state, params) {
      if (!Array.isArray(state.scenarios)) {
        return false;
      }
      const updatedScenarios = [...state.scenarios];
      const idx = updatedScenarios.findIndex((s) => s.id === params.id);
      updatedScenarios[idx] = {...updatedScenarios[idx], ...params};
      state.scenarios = [...updatedScenarios];
    },
    removeScenario(state, id) {
      const updatedScenarios = [...state.scenarios];
      const idx = updatedScenarios.findIndex((s) => s.id === id);
      updatedScenarios.splice(idx, 1);
      state.scenarios = [...updatedScenarios];
    },
    removeEvent(state, {id, scenarioId}) {
      const updatedScenarios = [...state.scenarios];
      const idx = updatedScenarios.findIndex((s) => s.id === scenarioId);
      const idxEv = updatedScenarios[idx].events.findIndex((e) => e.id === id);
      updatedScenarios[idx].events.splice(idxEv, 1);
      state.scenarios = [...updatedScenarios];
    },
    removeMultipleEvent(state, params) {
      let updatedScenarios = [...state.scenarios];
      updatedScenarios = updatedScenarios.map((s) => ({
        ...s,
        events: s.events.filter((e) => !params.events.includes(e.id)),
      }));
      if (params.event_id) {
        const scenarioIdx = updatedScenarios.findIndex(
          (s) => s.id === params.scenario_id,
        );
        const eventIdx = updatedScenarios[scenarioIdx].events.findIndex(
          (e) => e.id === params.event_id,
        );
        updatedScenarios[scenarioIdx].events[eventIdx].group_id = null;
      }
      state.scenarios = [...updatedScenarios];
    },
    addNotification(state, notification) {
      const idx = state.notifications.findIndex(
        (n) =>
          n.id === notification.id ||
          (n.msg === notification.msg && notification.once),
      );
      if (idx === -1) {
        state.notifications = [...state.notifications, notification];
      }
    },
    removeNotification(state, notification) {
      const idx = state.notifications.findIndex(
        (n) => n.id === notification.id,
      );
      if (idx !== -1) {
        state.notifications.splice(idx, 1);
      }
    },
    setDashboardSpecial(state, dashboardSpecial) {
      state.dashboardSpecial = dashboardSpecial;
    },
    setTooManyConnections(state, value) {
      state.tooManyConnections = value;
    },
    cleanNotifications(state) {
      state.notifications = [];
    },
    setAppLoaded(state, appLoaded) {
      state.appLoaded = appLoaded;
    },
    setTou(state, tou) {
      state.tou = tou;
    },
    setInAppMessage(state, inAppMessage) {
      state.inAppMessage = inAppMessage;
    },
    hideInAppMessage(state) {
      state.inAppMessage = {...state.inAppMessage, is_shown: true};
    },
    updateEvent(state, params) {
      const newEvent = {...params.event, cash_flow_series: [params.cash]};
      const updatedList = [...state.scenarios];
      const idx = updatedList.findIndex((s) => s.id === newEvent.scenario_id);
      const idxEv = updatedList[idx].events.findIndex(
        (e) => e.id === newEvent.id,
      );
      if (idxEv > -1) {
        if (params.cash) {
          updatedList[idx].events[idxEv] = newEvent;
        } else {
          updatedList[idx].events[idxEv] = {
            ...updatedList[idx].events[idxEv],
            ...newEvent,
            cash_flow_series: updatedList[idx].events[idxEv].cash_flow_series,
          };
        }
      } else {
        updatedList[idx].events.push(newEvent);
      }
      state.scenarios = [...updatedList];
    },
    setLoading(state, loading) {
      state.loading = loading;
    },
    setGlobalLoading(state, loading) {
      state.globalLoading = loading;
    },
    setLocked(state, locked) {
      state.locked = locked;
    },
    setReloading(state, reloading) {
      state.reloading = reloading;
    },
    setLoadingTitle(state, title) {
      state.loadingTitle = title;
    },
    setScenarios(state, scenarios) {
      state.scenarios = scenarios;
    },
    addScenario(state, scenario) {
      state.scenarios.push(scenario);
    },
    setCategoriesWithSubcategories(state, categories) {
      state.categories = categories;
    },
    addCategory(state, category) {
      const {subcategory, ...newCategory} = category;
      state.categories.push({...newCategory, subcategories: [subcategory]});
    },
    removeCategory(state, id) {
      const newCategories = [...state.categories];
      const idx = +newCategories.findIndex((c) => c.id === id);
      if (idx !== -1) {
        newCategories.splice(idx, 1);
        state.categories = [...newCategories];
      }
    },
    editCategory(state, {id, name}) {
      const newCategories = [...state.categories];
      const idx = +newCategories.findIndex((c) => c.id === id);
      if (idx !== -1) {
        newCategories[idx].name = name;
        state.categories = [...newCategories];
      }
    },
    editCategoryMutation(state, {id, name, categoryId}) {
      const newCategories = [...state.categories];
      const idx = +newCategories.findIndex((c) => c.id === categoryId);
      if (idx !== -1) {
        const subIdx = +newCategories[idx].subcategories.findIndex(
          (sc) => sc.id === id,
        );
        if (subIdx !== -1) {
          newCategories[idx].subcategories[subIdx].name = name;
          state.categories = [...newCategories];
        }
      }
    },
    showScenarios(state, show) {
      state.forceScenarios = show;
    },
  },
  actions: {
    actionAddNotification({commit}, message) {
      const notification = {id: window.utils.genId(), ...message};
      commit('addNotification', notification);
      setTimeout(() => {
        commit('removeNotification', notification);
      }, 8000);
    },
    async actionCreateTemplate() {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'POST',
        'createTemplate',
        {companyId},
        false,
        10,
        0,
        true,
      );
      return res;
    },
    async actionCreateDashboardTemplate(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'POST',
        'downloadDashboardTemplate',
        {companyId, period: params.period},
        false,
        10,
        0,
        true,
      );
      return res;
    },
    async actionImportDashboardXLSX(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      return await callAPI(
        'POST',
        'importDashboardXLSX',
        {
          ...params,
          companyId,
        },
        false,
        120,
        0,
      );
    },
    async actionUpdateCategoryForecast({commit}, categories) {
      const ids = await new Promise((resolve) => {
        const cids = recursiveEnabledCategories(categories);
        resolve(cids);
      });
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'POST',
        'updateCategoryForecast',
        {companyId, enabledIds: ids},
        false,
        60,
      );
      commit('updateCategoryForecast', categories);
      return res.result;
    },
    actionSetSilence({commit}) {
      commit('setSilence');
    },
    actionSuppressOnboarding({commit}, value) {
      commit('setSuppressOnboarding', value);
    },
    actionSetScalingCoeff({commit}, value) {
      commit('setScalingCoeff', value);
    },
    async actionGetTou({commit}) {
      const res = await callAPI('POST', 'getAppMeta', {});
      if (res.result) {
        commit('setTou', res.result);
      }
    },
    async actionGetInAppMessage({commit}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'getInAppMessage', {companyId});
      if (!res.error) {
        commit('setInAppMessage', res.result);
      }
      return res;
    },
    async actionHideInAppMessage({commit}) {
      commit('hideInAppMessage');
    },
    async actionAddInAppMessage({commit}, params) {
      const res = await callAPI('POST', 'addInAppMessage', params, true);
      if (!res.error) {
        commit('setInAppMessage', params);
      }
      return res;
    },
    async actionSetDashboardSpecial({commit}, val) {
      commit('setDashboardSpecial', val);
    },
    async actionUpdateTou(_, params) {
      await callAPI('POST', 'updateTou', params);
    },
    async actionUpdateBanking() {
      await callAPI('POST', 'update_banking_data', {}, true, 0);
    },
    async actionGetThreshold() {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'getThreshold', {companyId});
      return res;
    },
    async actionSetThreshold(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'setThreshold', {...params, companyId});
      return res;
    },
    async actionUpdateForecast() {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      await callAPI('POST', 'updateForecast', {companyId}, false, 0);
    },
    actionUnauthApp({commit}) {
      commit('unauthApp');
    },
    actionRemoveNotification({commit}, notification) {
      commit('removeNotification', notification);
    },
    actionShowScenarios({commit}, show) {
      commit('showScenarios', show);
    },
    actionSetTooManyConnections({commit}, value) {
      commit('setTooManyConnections', value);
    },
    actionCleanNotifications({commit}) {
      commit('cleanNotifications');
    },
    actionSetAppLoaded({commit}, appLoaded) {
      commit('setAppLoaded', appLoaded);
    },
    actionSetLoading({commit}, loading) {
      commit('setLoading', loading);
    },
    actionSetGlobalLoading({commit}, loading) {
      commit('setGlobalLoading', loading);
    },
    async actionSetSelectedCompany({commit}, selected) {
      commit('setSelectedCompany', selected);
    },
    actionSetLocked({commit}, loading) {
      commit('setLocked', loading);
    },
    actionSetReloading({commit}, reloading) {
      commit('setReloading', reloading);
    },
    actionSetLoadingTitle({commit}, title) {
      commit('setLoadingTitle', title);
    },
    actionSetBridgeCurrentItem({commit}, item) {
      commit('setBridgeCurrentItem', item);
    },
    async actionGetListCategories({commit}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('GET', `company/${companyId}/categoryTree`);
      if (!res.error) {
        commit('setCategoriesWithSubcategories', res.result.categories);
      }
      return res;
    },
    async actionUpdateCategoryType({commit}, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'PUT',
        `company/${companyId}/category/${params.categoryId}/type`,
        {
          type: params.type,
        },
      );
      if (!res.error) {
        commit('setCategoriesWithSubcategories', res.result.categories);
      }
      return res;
    },
    async actionUpdateCategoryParent({commit, dispatch}, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      let res;
      try {
        res = await callAPI('POST', 'category/parent', {
          ...params,
          companyId,
        });
      } catch (err) {
        dispatch(
          'app/actionAddNotification',
          {
            msg: categoryErrorMessage(err),
            color: 'red',
            disappearing: true,
          },
          {root: true},
        );
        return {};
      }
      commit('setCategoriesWithSubcategories', res.result.categories);
      return res.result;
    },
    async actionUpdateCategoryPosition({commit}, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'category/position', {
        ...params,
        companyId,
      });
      if (!res.error) {
        commit('setCategoriesWithSubcategories', res.result.categories);
      }
      return res;
    },
    async actionCopyCategory({commit}, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'duplicateCategory', {
        ...params,
        companyId,
      });
      commit('setCategoriesWithSubcategories', res.result.categories);
      return res;
    },
    async actionExcludeCategoryFromCalculation({commit}, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'PUT',
        `company/${companyId}/category/${params.categoryId}`,
        {not_include_in_income_or_expense: params.isActive},
      );
      commit('setCategoriesWithSubcategories', res.result.categories);
      return res;
    },
    async actionSetDefaultVatCategory({commit}, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'PUT',
        `company/${companyId}/setDefaultVatCategory/${params.categoryId}`,
      );
      commit('setCategoriesWithSubcategories', res.result.categories);
      return res;
    },
    async actionAddCategory({commit, dispatch}, {name, parentId, silent}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      let res;
      try {
        res = await callAPI('POST', 'addNewCategory', {
          name,
          parentId,
          companyId,
        });
      } catch (err) {
        // TODO remove catch after TT-3189
        // `silent` : le seed d'un tuto crée une catégorie d'exemple ; un échec
        // (ex. doublon resté d'un run interrompu) ne doit pas afficher le toast
        // d'erreur générique par-dessus le guide.
        if (!silent) {
          dispatch(
            'app/actionAddNotification',
            {
              msg: categoryErrorMessage(err, name),
              color: 'red',
              disappearing: true,
            },
            {root: true},
          );
        }
        console.warn(err);
        return;
      }
      if (res.message === 'show_complete') {
        dispatch('onboarding/actionSetChapterCompleted', 'add_categories', {
          root: true,
        });
      }
      commit('setCategoriesWithSubcategories', res.result.categories);
      return res.result;
    },
    async actionRemoveCategory({commit, dispatch}, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'removeCategory', {
        ...params,
        companyId,
      });
      commit('setCategoriesWithSubcategories', res.result.categories);
      // TODO : CAT : need to add a param with or without reindex
      await dispatch('bank/actionReindexTransactions', {}, {root: true});
      return res;
    },
    async actionEditCategory({dispatch, commit}, {categoryId, name}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      let res;
      // TODO remove catch after TT-3189
      try {
        res = await callAPI('POST', 'editCategory', {
          categoryId,
          name,
          companyId,
        });
      } catch (error) {
        dispatch(
          'app/actionAddNotification',
          {
            msg: categoryErrorMessage(error, name),
            color: 'red',
            disappearing: true,
          },
          {root: true},
        );
        console.warn(error);
        return {error: true};
      }
      commit('editCategory', {id: categoryId, name});

      if (res.error) {
        return res;
      }
      return {error: false};
    },
    async actionEditCategoryDetails(
      {commit},
      {
        id,
        name,
        categoryId,
        incInvInFDateFromInvFrequency,
        incInvInFDateFromInvFrequencyUnit,
        incInvInFDateToInvFrequency,
        incInvInFDateToInvFrequencyUnit,
        incInvInFDummy,
      },
    ) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      commit('editCategoryMutation', {id, name, categoryId});
      const res = await callAPI('POST', 'editCategoryDetails', {
        id,
        name,
        categoryId,
        incInvInFDateFromInvFrequency,
        incInvInFDateFromInvFrequencyUnit,
        incInvInFDateToInvFrequency,
        incInvInFDateToInvFrequencyUnit,
        incInvInFDummy,
        companyId,
      });
      if (res.error) {
        return res;
      }
      return {};
    },
    async actionCreateScenario({commit}, scenario) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      // Never send the string "null"/"undefined" as a color: the backend column is a UUID FK, so a
      // stringified null (e.g. picked from an existing colorless scenario) must become a real null.
      const color =
        scenario.color && !['null', 'undefined'].includes(scenario.color)
          ? scenario.color
          : null;
      const cleanScenario = {...scenario, color};
      const res = await callAPI('POST', 'createScenario', {
        scenario: {...cleanScenario, company_id: companyId},
        companyId,
      });
      commit('addScenario', {...cleanScenario, events: [], company_id: companyId});
      return res;
    },
    async actionUpdateEvent({commit}, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'updateEvent', {...params, companyId});
      commit('updateEvent', params);
      return res;
    },
    async actionCreateBackup(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'createBackup', {...params, companyId});
      return res;
    },
    async actionGetBackupList(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'getBackupList', {
        ...params,
        companyId,
      });
      return res;
    },
    async actionSetBackupStatus(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'setBackupStatus', {
        ...params,
        companyId,
      });
      return res;
    },
    async actionUpdateMultipleEvents(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      await callAPI('POST', 'updateMultipleEvents', {...params, companyId});
    },
    async actionGetColors({commit}) {
      const res = await callAPI('POST', 'getColors', {});
      if (res.result && res.result.colors) {
        commit('setColors', res.result.colors);
      }
    },
    async actionGetScenarios({commit}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'getScenarios', {companyId});
      if (res.result && res.result.scenarios) {
        commit('setScenarios', res.result.scenarios);
      }
    },
    actionRemoveScenarios({commit}) {
      commit('setScenarios', null);
    },
    async actionDeleteScenario({commit}, scenarioId) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      await callAPI('POST', 'deleteScenario', {scenarioId, companyId});
      commit('removeScenario', scenarioId);
    },
    async actionDeleteEvent({commit}, {id, scenarioId, should_invalidate_cache}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      await callAPI('POST', 'deleteEvent', {
        eventId: id,
        companyId,
        should_invalidate_cache,
      });
      commit('removeEvent', {id, scenarioId});
    },
    async actionDeleteMultipleEvents({commit}, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      await callAPI('POST', 'deleteMultipleEvents', {...params, companyId});
      commit('removeMultipleEvent', {...params});
    },
    async actionUpdateScenario({commit}, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      await callAPI('POST', 'updateScenario', {
        scenario: {...params, company_id: companyId},
        companyId,
      });
      commit('updateScenario', params);
    },
    async actionGetNumberOfCashFlows(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'getNumberOfCashFlows', {
        ...params,
        companyId,
      });
      if (res.result.number_of_cash_flows) {
        return res.result;
      }
      return res;
    },
    /* TODO: I have included this here but I wonder if all these functions
    would not deserve to be wrapped in different stores */
    async actionGetThetaWindow(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'getThetaWindow', {
        ...params,
        companyId,
      });
      return res;
    },
    async actionGetEndDate(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'getEndDate', {...params, companyId});
      if (res.result.end_date) {
        return res.result.end_date;
      }
      return null;
    },
    async actionCreateCFS({commit}, {updateEvent, ...params}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'create_cfs', {...params, companyId});
      if (updateEvent && !res.error) {
        commit('updateEvent', params);
      }
      return res;
    },
    async actionUpdateCFS({commit}, {updateEvent, ...params}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'update_cfs', {...params, companyId});
      if (updateEvent) {
        commit('updateEvent', params);
      }
      return res;
    },
    async actionDeleteCFS(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'delete_cfs', {...params, companyId});
      return res.result;
    },
    async actionGetCfsForbiddenRelativeCategories(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'get_cfs_forbidden_relative_categories', {...params, companyId});
      return res.result || [];
    },
    async actionGetCFSList(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'get_cfs_list', {...params, companyId});
      return res.result;
    },
    async actionGetCFSDetails(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'get_cfs_details', {
        ...params,
        companyId,
      });
      return res.result;
    },
    async actionGetCFSActual(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'get_actual', {...params, companyId});
      return res.result;
    },
    async actionUpdateCFSDescription(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      await callAPI('POST', 'update_cfs_description', {...params, companyId});
    },
    async actionUpdateCFSDisplayed(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      await callAPI('POST', 'update_cfs_displayed', {...params, companyId});
    },
    async actionGetCFSDescription(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'get_cfs_description', {
        ...params,
        companyId,
      });
      return res.result;
    },
    async actionAnalyzeCalibration() {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      // Le back parcourt 12 mois d'opérations sur toutes les catégories : les
      // 20 s par défaut sont trop courtes, et un rejeu doublerait l'attente.
      const res = await callAPI(
        'POST',
        `company/${companyId}/calibration/analyze`,
        {},
        false,
        60,
        1,
      );
      return res.result;
    },
    async actionStartCalibrationRun(_, payload) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'POST',
        `company/${companyId}/calibration/runs`,
        payload,
      );
      return res.result;
    },
    // Sondage toutes les 2 s : timeout court et surtout pas de rejeu, sinon un
    // appel lent s'empile derrière le tick suivant.
    async actionGetCalibrationRun(_, {runId}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'GET',
        `company/${companyId}/calibration/runs/${runId}`,
        {},
        false,
        10,
        1,
      );
      return res.result;
    },
    async actionGetLatestCalibrationRun() {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'GET',
        `company/${companyId}/calibration/runs/latest`,
        {},
        false,
        10,
        1,
      );
      return res.result;
    },
    async actionStopCalibrationRun(_, {runId}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'POST',
        `company/${companyId}/calibration/runs/${runId}/stop`,
      );
      return res.result;
    },
    async actionResumeCalibrationRun(_, {runId}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'POST',
        `company/${companyId}/calibration/runs/${runId}/resume`,
      );
      return res.result;
    },
    // Annulation d'un lot entier : suppressions, restaurations et réaffichages
    // côté back, puis nouvelle version des données. Long, d'où les 60 s.
    async actionRollbackCalibrationRun(_, {runId}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'POST',
        `company/${companyId}/calibration/runs/${runId}/rollback`,
        {},
        false,
        60,
        1,
      );
      return res.result;
    },
    async getAIGeneratedSuggestion(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      // `window_months` (3 | 6 | 12, 12 par défaut) est accepté par l'endpoint
      // mais inutile ici : la réponse porte déjà les moyennes des trois
      // fenêtres, le changement d'intervalle se fait donc sans aller-retour.
      const res = await callAPI(
        'POST',
        `company/${companyId}/cfs/previewFormula`,
        {
          category_id: params.category_id,
          account_ids: params.account_ids,
        },
      );
      return res.result;
    },
    // Applique la suggestion IA courante à la catégorie. Le back garantit une
    // seule formule IA par catégorie, l'appel est donc idempotent.
    async applyAiFormula(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'POST',
        `company/${companyId}/cfs/applyAiFormula`,
        {category_id: params.category_id},
      );
      return res.result;
    },
    async actionCreateCategoriesTemplate() {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'POST',
        'downloadCategoryTreeTemplate',
        {companyId},
        false,
        10,
        0,
        true,
      );
      return res;
    },
    async actionImportCategoriesXLSX(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      return await callAPI(
        'POST',
        'importCategoryTreeXLSX',
        {
          ...params,
          companyId,
        },
        false,
        120,
        0,
      );
    },
    async actionImportCategoryTreeFromCompany(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      await callAPI('POST', 'importCategoryTree', {...params, companyId}, false, 120, 1);
    },
    async actionImportRulesFromCompany(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      await callAPI('POST', 'importRulesList', {...params, companyId});
    },
    async actionCreateCategorizationRulesTemplate() {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'POST',
        'downloadSubCategoryRulesTemplate',
        {companyId},
        false,
        10,
        0,
        true,
      );
      return res;
    },
    async actionImportCategorizationRulesXLSX(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      return await callAPI(
        'POST',
        'importSubCategoryRulesXLSX',
        {
          ...params,
          companyId,
        },
        true,
        120,
        0,
      );
    },
  },
};
